import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, 
  User, 
  Lock, 
  Bell, 
  Shield, 
  Key, 
  Wallet, 
  Save, 
  AlertTriangle, 
  CheckCircle, 
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  Trash2,
  LogOut
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { initializeUserKeyPair, getUserPublicKeyBase64 } from '../../lib/crypto';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import toast from 'react-hot-toast';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  public_key: string | null;
  wallet_address: string | null;
  created_at: string;
  updated_at: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  documentShares: boolean;
  securityAlerts: boolean;
  productUpdates: boolean;
}

interface SecuritySettings {
  twoFactorEnabled: boolean;
  lastPasswordChange: string | null;
  sessionTimeout: number;
}

const SettingsPage: React.FC = () => {
  const { user, updateUser, logout } = useAuthStore();
  
  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    walletAddress: ''
  });
  
  // Password state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    documentShares: true,
    securityAlerts: true,
    productUpdates: false
  });
  const [isUpdatingNotifications, setIsUpdatingNotifications] = useState(false);
  
  // Security settings
  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    twoFactorEnabled: false,
    lastPasswordChange: null,
    sessionTimeout: 30
  });
  const [isUpdatingSecurity, setIsUpdatingSecurity] = useState(false);
  
  // Encryption keys
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [isGeneratingKeys, setIsGeneratingKeys] = useState(false);
  
  // Active section
  const [activeSection, setActiveSection] = useState<'profile' | 'security' | 'notifications' | 'encryption' | 'danger'>('profile');
  
  // Fetch user profile
  const fetchUserProfile = async () => {
    setIsLoadingProfile(true);
    
    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        throw error;
      }
      
      setProfile(data);
      setProfileForm({
        name: data.name || '',
        email: data.email || '',
        walletAddress: data.wallet_address || ''
      });
      
      // Fetch public key
      const publicKeyBase64 = await getUserPublicKeyBase64();
      setPublicKey(publicKeyBase64);
      
    } catch (error: any) {
      console.error('Failed to fetch user profile:', error);
      toast.error('Failed to load user profile');
    } finally {
      setIsLoadingProfile(false);
    }
  };
  
  // Update user profile
  const updateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    
    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      const { error } = await supabase
        .from('users')
        .update({
          name: profileForm.name,
          wallet_address: profileForm.walletAddress || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) {
        throw error;
      }
      
      // Update email if changed
      if (profileForm.email !== profile?.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: profileForm.email
        });
        
        if (emailError) {
          throw emailError;
        }
        
        toast.success('Email update initiated. Please check your inbox for verification.');
      }
      
      // Update local user state
      updateUser({
        name: profileForm.name,
        walletAddress: profileForm.walletAddress
      });
      
      // Refresh profile
      await fetchUserProfile();
      
      toast.success('Profile updated successfully');
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };
  
  // Update password
  const updatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsUpdatingPassword(true);
    
    try {
      // Update password
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      });
      
      if (error) {
        throw error;
      }
      
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      toast.success('Password updated successfully');
    } catch (error: any) {
      console.error('Failed to update password:', error);
      toast.error(error.message || 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };
  
  // Update notification settings
  const updateNotificationSettings = async () => {
    setIsUpdatingNotifications(true);
    
    try {
      // In a real implementation, this would save to the database
      // For now, we'll just simulate a successful update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast.success('Notification settings updated');
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification settings');
    } finally {
      setIsUpdatingNotifications(false);
    }
  };
  
  // Update security settings
  const updateSecuritySettings = async () => {
    setIsUpdatingSecurity(true);
    
    try {
      // In a real implementation, this would save to the database
      // For now, we'll just simulate a successful update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast.success('Security settings updated');
    } catch (error: any) {
      console.error('Failed to update security settings:', error);
      toast.error('Failed to update security settings');
    } finally {
      setIsUpdatingSecurity(false);
    }
  };
  
  // Generate new encryption keys
  const generateNewKeys = async () => {
    setIsGeneratingKeys(true);
    
    try {
      // Initialize new key pair
      await initializeUserKeyPair();
      
      // Get new public key
      const newPublicKey = await getUserPublicKeyBase64();
      setPublicKey(newPublicKey);
      
      // Update public key in database
      if (user?.id) {
        const { error } = await supabase
          .from('users')
          .update({
            public_key: newPublicKey,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
        
        if (error) {
          throw error;
        }
      }
      
      toast.success('New encryption keys generated successfully');
    } catch (error: any) {
      console.error('Failed to generate new keys:', error);
      toast.error(error.message || 'Failed to generate new keys');
    } finally {
      setIsGeneratingKeys(false);
    }
  };
  
  // Delete account
  const deleteAccount = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your data.'
    );
    
    if (!confirmed) {
      return;
    }
    
    try {
      // In a real implementation, this would delete the user's account
      // For now, we'll just log out the user
      toast.loading('Deleting account...', { id: 'delete-account' });
      
      // Simulate account deletion
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.success('Account deleted successfully', { id: 'delete-account' });
      
      // Log out the user
      await logout();
    } catch (error: any) {
      console.error('Failed to delete account:', error);
      toast.error(error.message || 'Failed to delete account', { id: 'delete-account' });
    }
  };
  
  // Initial data fetch
  useEffect(() => {
    if (user?.id) {
      fetchUserProfile();
    }
  }, [user?.id]);
  
  // Navigation items
  const navItems = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'encryption', label: 'Encryption Keys', icon: Key },
    { id: 'danger', label: 'Danger Zone', icon: AlertTriangle }
  ];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <Settings className="h-6 w-6 mr-2 text-primary-600" />
          Settings
        </h2>
        <p className="text-gray-600">
          Manage your account settings and preferences
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Navigation Sidebar */}
        <div className="lg:col-span-1">
          <Card className="p-4">
            <nav className="space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id as any)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-200
                    ${activeSection === item.id
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <item.icon size={18} className="flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </nav>
          </Card>
        </div>
        
        {/* Main Content */}
        <div className="lg:col-span-3">
          <Card className="p-6">
            {/* Profile Settings */}
            {activeSection === 'profile' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Profile Settings</h3>
                
                {isLoadingProfile ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
                  </div>
                ) : (
                  <form onSubmit={updateProfile} className="space-y-6">
                    <div className="space-y-4">
                      <Input
                        label="Full Name"
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        placeholder="Enter your full name"
                        required
                      />
                      
                      <Input
                        label="Email Address"
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        placeholder="Enter your email address"
                        required
                      />
                      
                      <Input
                        label="Wallet Address (Optional)"
                        type="text"
                        value={profileForm.walletAddress}
                        onChange={(e) => setProfileForm({ ...profileForm, walletAddress: e.target.value })}
                        placeholder="Enter your Ethereum wallet address"
                      />
                      
                      <div className="pt-4">
                        <Button
                          type="submit"
                          loading={isUpdatingProfile}
                          disabled={isUpdatingProfile}
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
              </motion.div>
            )}
            
            {/* Security Settings */}
            {activeSection === 'security' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Security Settings</h3>
                
                <div className="space-y-8">
                  {/* Password Change */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Change Password</h4>
                    <form onSubmit={updatePassword} className="space-y-4">
                      <div className="relative">
                        <Input
                          label="Current Password"
                          type={showPassword.current ? 'text' : 'password'}
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                          placeholder="Enter your current password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword({ ...showPassword, current: !showPassword.current })}
                          className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword.current ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      
                      <div className="relative">
                        <Input
                          label="New Password"
                          type={showPassword.new ? 'text' : 'password'}
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                          placeholder="Enter your new password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                          className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword.new ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      
                      <div className="relative">
                        <Input
                          label="Confirm New Password"
                          type={showPassword.confirm ? 'text' : 'password'}
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                          placeholder="Confirm your new password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                          className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      
                      <div>
                        <Button
                          type="submit"
                          loading={isUpdatingPassword}
                          disabled={isUpdatingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                        >
                          Update Password
                        </Button>
                      </div>
                    </form>
                  </div>
                  
                  {/* Two-Factor Authentication */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Two-Factor Authentication</h4>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">Coming Soon</h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>
                              Two-factor authentication will be available in a future update. This feature will add an extra layer of security to your account.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Enable Two-Factor Authentication</p>
                        <p className="text-xs text-gray-500">Require a verification code when signing in</p>
                      </div>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          type="checkbox"
                          id="toggle-2fa"
                          checked={securitySettings.twoFactorEnabled}
                          onChange={() => setSecuritySettings({
                            ...securitySettings,
                            twoFactorEnabled: !securitySettings.twoFactorEnabled
                          })}
                          disabled
                          className="checked:bg-primary-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 cursor-not-allowed appearance-none"
                        />
                        <label
                          htmlFor="toggle-2fa"
                          className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-not-allowed"
                        ></label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Session Settings */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Session Settings</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Session Timeout (minutes)
                        </label>
                        <select
                          value={securitySettings.sessionTimeout}
                          onChange={(e) => setSecuritySettings({
                            ...securitySettings,
                            sessionTimeout: parseInt(e.target.value)
                          })}
                          className="block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                        >
                          <option value={15}>15 minutes</option>
                          <option value={30}>30 minutes</option>
                          <option value={60}>1 hour</option>
                          <option value={120}>2 hours</option>
                          <option value={240}>4 hours</option>
                        </select>
                      </div>
                      
                      <div className="pt-2">
                        <Button
                          onClick={updateSecuritySettings}
                          loading={isUpdatingSecurity}
                          disabled={isUpdatingSecurity}
                        >
                          Save Security Settings
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Notification Settings */}
            {activeSection === 'notifications' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Notification Settings</h3>
                
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Email Notifications</p>
                        <p className="text-xs text-gray-500">Receive notifications via email</p>
                      </div>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          type="checkbox"
                          id="toggle-email"
                          checked={notificationSettings.emailNotifications}
                          onChange={() => setNotificationSettings({
                            ...notificationSettings,
                            emailNotifications: !notificationSettings.emailNotifications
                          })}
                          className="checked:bg-primary-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 cursor-pointer appearance-none"
                        />
                        <label
                          htmlFor="toggle-email"
                          className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                        ></label>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Document Shares</p>
                        <p className="text-xs text-gray-500">Notify when documents are shared with you</p>
                      </div>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          type="checkbox"
                          id="toggle-shares"
                          checked={notificationSettings.documentShares}
                          onChange={() => setNotificationSettings({
                            ...notificationSettings,
                            documentShares: !notificationSettings.documentShares
                          })}
                          className="checked:bg-primary-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 cursor-pointer appearance-none"
                        />
                        <label
                          htmlFor="toggle-shares"
                          className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                        ></label>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Security Alerts</p>
                        <p className="text-xs text-gray-500">Receive alerts about security events</p>
                      </div>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          type="checkbox"
                          id="toggle-security"
                          checked={notificationSettings.securityAlerts}
                          onChange={() => setNotificationSettings({
                            ...notificationSettings,
                            securityAlerts: !notificationSettings.securityAlerts
                          })}
                          className="checked:bg-primary-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 cursor-pointer appearance-none"
                        />
                        <label
                          htmlFor="toggle-security"
                          className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                        ></label>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Product Updates</p>
                        <p className="text-xs text-gray-500">Receive updates about new features</p>
                      </div>
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          type="checkbox"
                          id="toggle-updates"
                          checked={notificationSettings.productUpdates}
                          onChange={() => setNotificationSettings({
                            ...notificationSettings,
                            productUpdates: !notificationSettings.productUpdates
                          })}
                          className="checked:bg-primary-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 border-gray-300 cursor-pointer appearance-none"
                        />
                        <label
                          htmlFor="toggle-updates"
                          className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                        ></label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button
                      onClick={updateNotificationSettings}
                      loading={isUpdatingNotifications}
                      disabled={isUpdatingNotifications}
                    >
                      Save Notification Settings
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Encryption Keys */}
            {activeSection === 'encryption' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Encryption Keys</h3>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <Shield className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">End-to-End Encryption</h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>
                            Aegis Vault uses RSA-2048 and AES-256 encryption to secure your documents. Your private key never leaves your device, ensuring that only you can access your encrypted documents.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Your Public Key</h4>
                    {isLoadingProfile ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-6 w-6 text-primary-500 animate-spin" />
                      </div>
                    ) : publicKey ? (
                      <div className="space-y-3">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium text-gray-700">Public Key (RSA-2048)</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Eye}
                              onClick={() => {
                                navigator.clipboard.writeText(publicKey);
                                toast.success('Public key copied to clipboard');
                              }}
                              className="h-6 w-6 p-0"
                            />
                          </div>
                          <p className="text-xs font-mono break-all text-gray-600">
                            {publicKey.substring(0, 64)}...
                          </p>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                          Your encryption keys are properly set up
                        </div>
                        
                        <div className="pt-2">
                          <Button
                            variant="outline"
                            icon={RefreshCw}
                            onClick={generateNewKeys}
                            loading={isGeneratingKeys}
                            disabled={isGeneratingKeys}
                          >
                            Generate New Keys
                          </Button>
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                          <div className="flex items-start">
                            <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
                            <div>
                              <p className="text-sm font-medium text-yellow-800">Important</p>
                              <p className="text-xs text-yellow-700 mt-1">
                                Generating new keys will prevent you from accessing previously encrypted documents. Only do this if you're sure you want to reset your encryption keys.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-start">
                            <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
                            <div>
                              <p className="text-sm font-medium text-yellow-800">No Encryption Keys Found</p>
                              <p className="text-xs text-yellow-700 mt-1">
                                You need to generate encryption keys to use secure document sharing features.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="pt-2">
                          <Button
                            icon={Key}
                            onClick={generateNewKeys}
                            loading={isGeneratingKeys}
                            disabled={isGeneratingKeys}
                          >
                            Generate Encryption Keys
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Private Key Security</h4>
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">
                        Your private key is stored securely in your browser's local storage and is never sent to our servers. This ensures that only you can decrypt your documents.
                      </p>
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-900 mb-2">Important Security Notes:</h5>
                        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                          <li>Never share your private key with anyone</li>
                          <li>Clearing your browser data will delete your private key</li>
                          <li>Using a different device or browser requires key synchronization</li>
                          <li>We recommend backing up your keys in a secure password manager</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Danger Zone */}
            {activeSection === 'danger' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Danger Zone</h3>
                
                <div className="space-y-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-red-800 mb-4">Delete Account</h4>
                    <p className="text-sm text-red-700 mb-4">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                    <div className="space-y-4">
                      <div className="bg-white border border-red-200 rounded-lg p-3">
                        <ul className="text-sm text-red-700 space-y-2 list-disc list-inside">
                          <li>All your documents will be permanently deleted</li>
                          <li>Your encryption keys will be lost</li>
                          <li>Shared documents will become inaccessible</li>
                          <li>Your profile and settings will be removed</li>
                        </ul>
                      </div>
                      
                      <Button
                        variant="outline"
                        icon={Trash2}
                        onClick={deleteAccount}
                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        Delete My Account
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-orange-800 mb-4">Sign Out Everywhere</h4>
                    <p className="text-sm text-orange-700 mb-4">
                      Sign out from all devices where you're currently logged in.
                    </p>
                    <Button
                      variant="outline"
                      icon={LogOut}
                      onClick={async () => {
                        try {
                          await supabase.auth.signOut({ scope: 'global' });
                          toast.success('Signed out from all devices');
                          setTimeout(() => {
                            window.location.href = '/auth';
                          }, 1500);
                        } catch (error) {
                          console.error('Failed to sign out:', error);
                          toast.error('Failed to sign out from all devices');
                        }
                      }}
                      className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                    >
                      Sign Out Everywhere
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;